import React, { useState } from "react";
import { lazy, Suspense, useEffect } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";
import { LayoutSplashScreen } from "../Utils/layout";
import SSO from "./modules/Auth/pages/SSO";
import { Thankyou } from "./modules/Auth/pages/Thankyou";
import { actions } from "./modules/Auth/_redux/authRedux";
import DocsLink from "./modules/Docs/pages/DocsLink";
import * as helpers from "./util/helpers";
import CustomizedSnackbars from "./util/Snackbar";
import { SnackbarProvider } from "notistack";
import SnackbarCloseButton from "./util/SnackbarCloseButton";
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import CheckCircleOutlinedIcon from '@material-ui/icons/CheckCircleOutlined';
import { DeveloperDocs } from "./modules/DeveloperDoc/pages/DeveloperDocs";

const UserProfilepage = lazy(() =>
  import("./modules/UserProfile/UserProfilePage")
);
const DashboardPage = lazy(() =>
  import("./modules/Dashboard/pages/DashboardPage")
);
const InsightsPage = lazy(() =>
  import("./modules/Insights/pages/InsightsPage")
);
const LiveInteractionsPage = lazy(() =>
  import("./modules/LiveInteractions/pages/LiveInteractionsPage")
);
const SMSLogs = lazy(() => import("./modules/SMSLogs/index"));
const CallLogs = lazy(() => import("./modules/CallLogs/index"));
const CRM = lazy(() => import("./modules/CRM/index"));
const CRMView = lazy(() => import("./modules/CRM/indexView"));
const InboundQueue = lazy(() => import("./modules/InboundQueue/index"));
const ConfigurationPage = lazy(() =>
  import("./modules/Configuration/pages/ConfigurationPage")
);
const SettingsPage = lazy(() =>
  import("./modules/Settings/pages/SettingsPage")
);
const CampaignPage = lazy(() =>
  import("./modules/Campaign/pages/CampaignPage")
);
const MyAccountPage = lazy(() =>
  import("./modules/MyAccount/pages/MyAccountPage")
);
const HierarchyPage = lazy(() =>
  import("./modules/Hierarchy")
);
const Home = lazy(() =>
  import("./modules/Home")
);

export default function BasePage() {
  const dispatch = useDispatch();
  const location = useLocation();
  
  const [shouldRenderIframe, setShouldRenderIframe] = useState(false);

  let redirect_url = localStorage.getItem("redirect_url");
  let temp_token = localStorage.getItem("temp_token");

  // modules which will always be rendered in NewSr view
  const newModules = ['/reports', '/live-interactions/queues-monitor', '/home', '/customer-management/customer'];

  // loading OldSr Iframe in optimised way, lazily.
  const LazyIframeComponent = lazy(() => import('./components/OldSRIframeModule'));

  const { reloaded } = useSelector(
    ({ auth }) => ({
      reloaded: auth.reloaded,
    }),
    shallowEqual
  );

  // A Redux Ref., which has all toggled routes for which we are rendering OldSR iframe.
  const getOldSwitchState = useSelector(state => state.auth.switch_old_state);
  let assumeId = localStorage.getItem("assume_id");
  assumeId = (assumeId === 'null' || assumeId === '' || assumeId === 'None') ? false : assumeId;
  let basePath = '(phonebooks|dispositions|sounds|reports|templates|sticky-agent)';
  useEffect(() => {
    if (reloaded !== 1) {
      dispatch(actions.userDetail({}));
    }
  }, []);

  useEffect(() => {
    setShouldRenderIframe(shouldRenderDynamicIframeComponent());
  }, [location, shouldRenderDynamicIframeComponent()]);

  // Selection of Component, OldSR/NewSR rendering on case basis.
  function shouldRenderDynamicIframeComponent() {
    const switchOldState = JSON.parse(localStorage.getItem('switch_old_state'));
    const oldState = (switchOldState && switchOldState[window.location.pathname]) || getOldSwitchState[window.location.pathname];
    return (
      oldState &&
      !newModules.includes(window.location.pathname) &&
      !helpers.isFeatureEnabledProfile('hide_iframe_redirection')
    );
  }

  const DynamicIframeComponent = ({ ...rest }) => (
    <Suspense fallback={<div>Loading...</div>}><LazyIframeComponent {...rest} /></Suspense>
  );

  return (
    <Suspense fallback={<LayoutSplashScreen />}>    
       <CustomizedSnackbars />
      <SnackbarProvider
        maxSnack={5}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        action={(snackbarKey) => (
          <SnackbarCloseButton snackbarKey={snackbarKey} />
        )}
        iconVariant={{
          error: <ErrorOutlineIcon style={{ height: 22, width: 22, marginRight: 8 }} />, 
          success: <CheckCircleOutlinedIcon style={{ height: 24, width: 24, marginRight: 8 }}/>
        }}
      >

        <Switch>
     
          {<Redirect exact from="/" to="/home" />}
          <Route path="/user-profile" component={UserProfilepage} />
          {!helpers.isFeatureEnabledProfile('is_suspended_account') && 
            <Route path="/dashboard" component={DashboardPage} />
          }
          <Route path="/home" component={Home} />

          {!helpers.isAgent() && (
            <Route path="/insights" component={InsightsPage} />
          )}

          {helpers.isAgent() && helpers.isCallCenterEnabled() &&
            helpers.isFeatureEnabled("queue_monitoring") && (
              <Route
                path="/live-interactions/queues-monitor"
                component={shouldRenderIframe ? DynamicIframeComponent : InboundQueue} 
              />
          )}

          {!helpers.isAgent() && 
            !helpers.isFeatureEnabledProfile('is_suspended_account') && 
            helpers.isCallCenterEnabled() &&
            (helpers.isFeatureEnabled("is_call_monitor") || helpers.isFeatureEnabled("is_queue_monitor")) && (
              <Route
                path="/live-interactions"
                component={shouldRenderIframe ? DynamicIframeComponent : LiveInteractionsPage} 
              />
          )}

          {helpers.isFeatureEnabled("sms_logs") && helpers.isFeatureEnabledProfile("is_sms_enabled") && (
            <Route path="/smslogs" component={shouldRenderIframe ? DynamicIframeComponent : SMSLogs}  />
          )}

          {helpers.isFeatureEnabled("call_logs") && !helpers.isFeatureEnabledProfile('is_suspended_account') && (
            <Route path="/call-logs" component={shouldRenderIframe ? DynamicIframeComponent : CallLogs}  />
          )}

          {helpers.isFeatureEnabled("customer_management") && !helpers.isFeatureEnabledProfile('is_suspended_account') && (
            <Route
              path="/customer-management/customer/:id"
              component={CRMView} 
            />
          )}

          {helpers.isFeatureEnabled("customer_management") && !helpers.isFeatureEnabledProfile('is_suspended_account') && (
            <Route path="/customer-management" component={shouldRenderIframe ? DynamicIframeComponent : CRM}  />
          )}

          {helpers.isFeatureEnabled("customer_management") && !helpers.isFeatureEnabledProfile('is_suspended_account') && (
            <Route path="/customer-management-view" component={CRMView} />
          )}

          <Route path="/docs" component={DocsLink} />

          
          {!helpers.isAgent() && 
          !helpers.isFeatureEnabledProfile('is_suspended_account') &&
          <Route path="/developerdocs" component={DeveloperDocs} />}

          {!helpers.isAgent() && 
          helpers.isFeatureEnabledProfile('is_obd_allowed') && 
          !helpers.isFeatureEnabledProfile('is_suspended_account') &&
          helpers.isUIParamsAvailable('campaign') && !helpers.isFeatureEnabledProfile('hide_iframe_redirection') &&
          <Route path="/(createcampaign|campaign)" component={shouldRenderIframe ? DynamicIframeComponent : CampaignPage} />}

          {(helpers.isAdmin() && helpers.isUIParamsAvailable('account')) && !helpers.isFeatureEnabledProfile('hide_iframe_redirection') &&
          <Route path="/(settings|account_profile|history|billstatement|invoices|payments|billing_summary)" component={shouldRenderIframe ? DynamicIframeComponent : MyAccountPage} />
          }

          {!helpers.isAgent() &&
            (helpers.isFeatureEnabled("is_phonebook_tab_enabled") ||
              helpers.isFeatureEnabled("is_reports_enabled") ||
              (helpers.is_disposition_enabled() &&
                helpers.isFeatureEnabled("is_disposition_tab_enabled")) ||
              helpers.isFeatureEnabled("is_stickynote_tab_enabled")) && (
              <Route
                path={`/${basePath}`}
                component={shouldRenderIframe ? DynamicIframeComponent : ConfigurationPage} 
              />
            )}

          {!helpers.isAgent() && (helpers.isFeatureEnabled("is_hook_api_enabled") || helpers.isFeatureEnabled('is_agent_setting_enabled')) && (
            <Route path="/(hookapi|agentsettings|queueSettings|callflow|smsflow|callgroups|integrations)" component={shouldRenderIframe ? DynamicIframeComponent : SettingsPage} />
          )}

          {helpers.isHierarchyEnabledAccount() && (
            <Route path="/hierarchy" component={HierarchyPage} />
          )}

          <Route path="/thankyou" component={Thankyou} />
          <Route path="/sso" component={SSO} />
          {/* {((!redirect_url && temp_token) || redirect_url) && <Redirect from="/" to={redirect_url || "/home"} />} */}
          <Redirect to="/home" />
        </Switch>
      </SnackbarProvider>
    </Suspense>
  );
}
